import React from "react";
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  FlagIcon,
  XCircleIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  VideoCameraIcon,
  PencilSquareIcon,
  BellAlertIcon,
  ShareIcon,
  UserPlusIcon,
  CogIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon
} from "@heroicons/react/24/outline";
import { getLocalDateTime, formatDisplayDate } from "../../../utils/dateUtils";
// Custom date formatting utilities

const RecentActivity = ({ activities = [] }) => {
  // Function to get activity icon based on type and details
  const getActivityIcon = (activityType, details = '') => {
    // Handle undefined or null activityType
    if (!activityType) {
      return {
        icon: ClockIcon,
        bgColor: 'bg-gray-100 dark:bg-gray-900/30',
        iconColor: 'text-gray-600 dark:text-gray-400'
      };
    }

    const type = activityType.toLowerCase();
    const detailsLower = (details || '').toLowerCase();

    // Stage Change activities with specific icons based on details
    if (type.includes('stage')) {
      if (detailsLower.includes('interested')) {
        return {
          icon: CheckCircleIcon,
          bgColor: 'bg-green-100 dark:bg-green-900/30',
          iconColor: 'text-green-600 dark:text-green-400'
        };
      }
      if (detailsLower.includes('enquiry') || detailsLower.includes('inquiry')) {
        return {
          icon: InformationCircleIcon,
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: 'text-blue-600 dark:text-blue-400'
        };
      }
      if (detailsLower.includes('closed') || detailsLower.includes('won')) {
        return {
          icon: FlagIcon,
          bgColor: 'bg-green-100 dark:bg-green-900/30',
          iconColor: 'text-green-600 dark:text-green-400'
        };
      }
      if (detailsLower.includes('lost') || detailsLower.includes('rejected')) {
        return {
          icon: XCircleIcon,
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          iconColor: 'text-red-600 dark:text-red-400'
        };
      }
      // Default stage change
      return {
        icon: ArrowPathIcon,
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        iconColor: 'text-blue-600 dark:text-blue-400'
      };
    }

    // Communication activities
    if (type.includes('email') || type.includes('mail')) {
      return {
        icon: EnvelopeIcon,
        bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
        iconColor: 'text-indigo-600 dark:text-indigo-400'
      };
    }

    if (type.includes('call') || type.includes('phone')) {
      return {
        icon: PhoneIcon,
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        iconColor: 'text-purple-600 dark:text-purple-400'
      };
    }

    if (type.includes('message') || type.includes('sms') || type.includes('text')) {
      return {
        icon: PaperAirplaneIcon,
        bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
        iconColor: 'text-cyan-600 dark:text-cyan-400'
      };
    }

    if (type.includes('chat') || type.includes('whatsapp')) {
      return {
        icon: ChatBubbleLeftRightIcon,
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        iconColor: 'text-green-600 dark:text-green-400'
      };
    }

    if (type.includes('video') || type.includes('meeting') || type.includes('demo')) {
      return {
        icon: VideoCameraIcon,
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        iconColor: 'text-orange-600 dark:text-orange-400'
      };
    }

    // Document and task activities
    if (type.includes('note') || type.includes('comment') || type.includes('remark')) {
      return {
        icon: PencilSquareIcon,
        bgColor: 'bg-gray-100 dark:bg-gray-900/30',
        iconColor: 'text-gray-600 dark:text-gray-400'
      };
    }

    if (type.includes('follow') || type.includes('reminder')) {
      return {
        icon: BellAlertIcon,
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400'
      };
    }

    if (type.includes('schedule') || type.includes('calendar') || type.includes('appointment')) {
      return {
        icon: CalendarDaysIcon,
        bgColor: 'bg-teal-100 dark:bg-teal-900/30',
        iconColor: 'text-teal-600 dark:text-teal-400'
      };
    }

    // Default fallback
    return {
      icon: ClockIcon,
      bgColor: 'bg-gray-100 dark:bg-gray-900/30',
      iconColor: 'text-gray-600 dark:text-gray-400'
    };
  };

  // Function to format activity details
  const formatActivityDetails = (details) => {
    if (!details) return "Activity performed";

    // Clean up the details text
    let cleanDetails = details;

    // Remove "Stage updated to:" prefix if present
    if (cleanDetails.includes("Stage updated to:")) {
      cleanDetails = cleanDetails.replace("Stage updated to:", "").trim();
    }

    // Extract remarks if present
    const remarksMatch = cleanDetails.match(/\. Remarks: (.+)$/);
    if (remarksMatch) {
      const mainText = cleanDetails.replace(/\. Remarks: .+$/, "");
      const remarks = remarksMatch[1];
      return {
        main: mainText,
        remarks: remarks
      };
    }

    return { main: cleanDetails, remarks: null };
  };

  // Function to format display time with smart formatting
  const formatDisplayTime = (dateString) => {
    return formatDisplayDate(dateString);
  };

  // Sort activities by date (most recent first)
  const sortedActivities = activities
    .sort((a, b) => getLocalDateTime(b.ActivityDate) - getLocalDateTime(a.ActivityDate))
    .slice(0, 10); // Show only the most recent 10 activities

  if (!sortedActivities.length) {
    return (
      <div className="text-center py-6">
        <ClockIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No recent activities found
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
        <ClockIcon className="h-4 w-4" />
        Recent Activity
        <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
          ({sortedActivities.length} activities)
        </span>
      </h4>

      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {sortedActivities.map((activity, index) => {
          const { icon: IconComponent, bgColor, iconColor } = getActivityIcon(activity.ActivityType, activity.Details);
          const { main, remarks } = formatActivityDetails(activity.Details);

          return (
            <div
              key={activity.ActivityId || index}
              className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${bgColor} shrink-0`}>
                <IconComponent className={`h-4 w-4 ${iconColor}`} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-900 dark:text-white">
                      {activity.ActivityType || "Activity"}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-relaxed">
                      {main}
                    </p>
                    {remarks && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        Note: {remarks}
                      </p>
                    )}
                  </div>

                  <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                    {formatDisplayTime(activity.ActivityDate)}
                  </span>
                </div>

                {activity.DoneBy && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <UserIcon className="inline h-3 w-3 mr-1" />
                    By {activity.DoneBy}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {activities.length > 10 && (
        <div className="text-center pt-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing 10 of {activities.length} activities
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
